import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './UserProfile.css';

function UserProfile() {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('profile');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('https://trcsite.net/api/auth/profile', 
        { name: formData.name },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage('Profile updated successfully!');
      updateUser(response.data.user);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('https://trcsite.net/api/auth/change-password',
        {
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      }

      setMessage('Password changed successfully. Other devices have been signed out.');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('https://trcsite.net/api/auth/resend-verification',
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage('Verification email sent! Please check your inbox.');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send verification email');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const token = localStorage.getItem('token');
      await axios.post('https://trcsite.net/api/auth/change-email',
        { newEmail: formData.email },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setMessage('Verification email sent to your new email address. Please verify to complete the change.');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to change email');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: { label: 'Administrator', class: 'role-admin', icon: '👑' },
      moderator: { label: 'Moderator', class: 'role-moderator', icon: '🛡️' },
      user: { label: 'User', class: 'role-user', icon: '👤' }
    };
    
    const config = roleConfig[role] || roleConfig.user;
    return (
      <span className={`role-badge ${config.class}`}>
        <span className="role-icon">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const getKycStatusBadge = (status) => {
    const statusConfig = {
      verified: { label: 'Verified', class: 'kyc-verified', icon: '✅' },
      approved: { label: 'Approved', class: 'kyc-approved', icon: '✅' },
      pending: { label: 'Pending', class: 'kyc-pending', icon: '⏳' },
      rejected: { label: 'Rejected', class: 'kyc-rejected', icon: '❌' }
    };
    
    const config = statusConfig[status] || { label: 'Not Started', class: 'kyc-not-started', icon: '📝' };
    return (
      <span className={`kyc-badge ${config.class}`}>
        <span className="kyc-icon">{config.icon}</span>
        {config.label}
      </span>
    );
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-error">
          Please log in to view your profile.
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div className="user-identity">
          <div className="user-avatar">
            {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div className="user-info">
            <h1>{user.name || 'User'}</h1>
            <div className="user-meta">
              <span className="user-uid">UID: {user.uid || 'N/A'}</span>
              {getRoleBadge(user.role)}
            </div>
          </div>
        </div>
        <p>Manage your account settings and preferences</p>
      </div>

      {!user.is_verified && (
        <div className="verification-banner">
          <div className="verification-content">
            <span className="verification-icon">⚠️</span>
            <div className="verification-text">
              <strong>Email not verified</strong>
              <p>Optional: verify your email so we can reach you for account recovery</p>
            </div>
            <button 
              onClick={handleResendVerification}
              className="verify-btn"
              disabled={loading}
            >
              {loading ? 'Sending...' : 'Resend Verification'}
            </button>
          </div>
        </div>
      )}

      {message && <div className="profile-message">{message}</div>}
      {error && <div className="profile-error">{error}</div>}

      <div className="profile-content">
        <div className="profile-sidebar">
          <button 
            className={`sidebar-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            <span className="tab-icon">👤</span>
            Profile Info
          </button>
          <button 
            className={`sidebar-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <span className="tab-icon">🔒</span>
            Security
          </button>
          <button 
            className={`sidebar-tab ${activeTab === 'email' ? 'active' : ''}`}
            onClick={() => setActiveTab('email')}
          >
            <span className="tab-icon">📧</span>
            Email Settings
          </button>
        </div>

        <div className="profile-main">
          {activeTab === 'profile' && (
            <div className="profile-section">
              <h2>Profile Information</h2>
              
              <div className="user-identity-card">
                <div className="identity-header">
                  <h3>Account Identity</h3>
                </div>
                <div className="identity-details">
                  <div className="identity-row">
                    <span className="identity-label">User ID:</span>
                    <span className="identity-value">{user.id}</span>
                  </div>
                  <div className="identity-row">
                    <span className="identity-label">Unique ID (UID):</span>
                    <span className="identity-value uid-display">{user.uid || 'Not assigned'}</span>
                  </div>
                  <div className="identity-row">
                    <span className="identity-label">Account Role:</span>
                    <span className="identity-value">{getRoleBadge(user.role)}</span>
                  </div>
                </div>
              </div>

              <div className="profile-stats">
                <div className="stat-card">
                  <div className="stat-icon">📅</div>
                  <div className="stat-info">
                    <span className="stat-label">Member Since</span>
                    <span className="stat-value">
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">✅</div>
                  <div className="stat-info">
                    <span className="stat-label">Email Verification</span>
                    <span className={`stat-value ${user.is_verified ? 'verified' : 'unverified'}`}>
                      {user.is_verified ? 'Verified' : 'Unverified'}
                    </span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🔐</div>
                  <div className="stat-info">
                    <span className="stat-label">KYC Status</span>
                    <span className="stat-value">
                      {getKycStatusBadge(user.kyc_status)}
                    </span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">🆔</div>
                  <div className="stat-info">
                    <span className="stat-label">Account Type</span>
                    <span className="stat-value">
                      {user.role === 'admin' ? 'Administrator' : 
                       user.role === 'moderator' ? 'Moderator' : 'Standard User'}
                    </span>
                  </div>
                </div>
              </div>

              <form onSubmit={handleProfileUpdate} className="profile-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Enter your email address"
                    disabled
                    className="disabled-input"
                  />
                  <small>To change email, use the Email Settings tab</small>
                </div>

                <div className="form-group">
                  <label htmlFor="uid">Unique ID (UID)</label>
                  <input
                    type="text"
                    id="uid"
                    value={user.uid || 'Not assigned'}
                    disabled
                    className="disabled-input uid-input"
                  />
                  <small>Your unique identifier across the platform</small>
                </div>

                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={loading || !formData.name.trim()}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <div className="profile-section">
              <h2>Account Security</h2>
              <p className="section-description">
                Update your password to keep your account secure
              </p>

              <div className="security-info">
                <h3>Security Status</h3>
                <div className="security-status">
                  <div className="security-item">
                    <span className="security-icon">📧</span>
                    <div className="security-details">
                      <span className="security-label">Email Verification</span>
                      <span className={`security-value ${user.is_verified ? 'secure' : 'insecure'}`}>
                        {user.is_verified ? 'Verified' : 'Not Verified'}
                      </span>
                    </div>
                  </div>
                  <div className="security-item">
                    <span className="security-icon">🆔</span>
                    <div className="security-details">
                      <span className="security-label">Account ID</span>
                      <span className="security-value uid-value">{user.uid || 'Not assigned'}</span>
                    </div>
                  </div>
                  <div className="security-item">
                    <span className="security-icon">👑</span>
                    <div className="security-details">
                      <span className="security-label">Account Role</span>
                      <span className="security-value">{user.role}</span>
                    </div>
                  </div>
                </div>
              </div>

              <form onSubmit={handlePasswordChange} className="profile-form">
                <h3>Change Password</h3>
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    placeholder="Enter current password"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    placeholder="Enter new password (min. 6 characters)"
                    minLength="6"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <button 
                  type="submit" 
                  className="save-btn"
                  disabled={loading || !formData.currentPassword || !formData.newPassword || !formData.confirmPassword}
                >
                  {loading ? 'Updating...' : 'Change Password'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'email' && (
            <div className="profile-section">
              <h2>Email Settings</h2>
              
              <div className="current-email">
                <h3>Current Email</h3>
                <div className="email-display">
                  <div className="email-info">
                    <span className="email-address">{user.email}</span>
                    <span className={`email-status ${user.is_verified ? 'verified' : 'unverified'}`}>
                      {user.is_verified ? '✓ Verified' : '✗ Unverified'}
                    </span>
                  </div>
                  <div className="account-id">
                    <span className="id-label">Account ID:</span>
                    <span className="id-value">{user.uid || 'Not assigned'}</span>
                  </div>
                </div>
              </div>

              {!user.is_verified && (
                <div className="verification-prompt">
                  <h4>Verify Your Email</h4>
                  <p>Please verify your email address to access all platform features and receive important notifications.</p>
                  <button 
                    onClick={handleResendVerification}
                    className="verify-btn secondary"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}

              <div className="change-email-section">
                <h3>Change Email Address</h3>
                <p>Enter your new email address below. We'll send a verification link to complete the process.</p>
                
                <form onSubmit={handleEmailChange} className="profile-form">
                  <div className="form-group">
                    <label htmlFor="newEmail">New Email Address</label>
                    <input
                      type="email"
                      id="newEmail"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Enter new email address"
                      required
                    />
                  </div>

                  <button 
                    type="submit" 
                    className="save-btn"
                    disabled={loading || !formData.email || formData.email === user.email}
                  >
                    {loading ? 'Sending...' : 'Send Verification Email'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserProfile;